import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
  try {
    const u = JSON.parse(localStorage.getItem("userElwnsh") || "{}");
    if (u?.token) return u.token;
  } catch (_) {
    /* ignore */
  }
  return localStorage.getItem("token");
};

// Fetch all winch partner contracts (optional query filters)
export const fetchWinchPartnerContracts = createAsyncThunk(
  "winch_partner_contracts/fetchAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const queryParams = new URLSearchParams();
      const add = (key, val) => {
        if (val === undefined || val === null) return;
        if (typeof val === "string" && val.trim() === "") return;
        queryParams.append(key, String(val).trim());
      };

      add("contract_number", filters.contract_number);
      add("winch_partner_id", filters.winch_partner_id);
      add("contract_type", filters.contract_type);
      add("auto_renewal", filters.auto_renewal);
      add("status", filters.status);
      add("date_filter", filters.date_filter);
      if (filters.date_filter === "range") {
        if (filters.from_date) {
          queryParams.append("from_date[0]", String(filters.from_date).trim());
        }
        add("to_date", filters.to_date);
      }
      if (filters.quick_filter) {
        add("quick_filter", filters.quick_filter);
      }

      const qs = queryParams.toString();
      const url = `${BASE_URL}/partner-contracts${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Fetch business sector contracts for dropdown (for Contract Addendum)
export const fetchBusinessSectorContractsForDropdown = createAsyncThunk(
  "winch_partner_contracts/fetchBusinessSectorContracts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-contracts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Handle response structure
      if (response.data?.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      return [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sector contracts";
      return rejectWithValue(message);
    }
  }
);

// Fetch winch partner contracts for dropdown (for Contract Addendum)
export const fetchWinchPartnerContractsForDropdown = createAsyncThunk(
  "winch_partner_contracts/fetchWinchPartnerContracts",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${BASE_URL}/partner-contracts`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Handle response structure
      if (response.data?.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      return [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partner contracts";
      return rejectWithValue(message);
    }
  }
);

// Add winch partner contract
export const addWinchPartnerContract = createAsyncThunk(
  "winch_partner_contracts/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all form fields
      if (formData.winch_partner_id) data.append("winch_partner_id", formData.winch_partner_id);
      if (formData.contract_type) data.append("contract_type", formData.contract_type);
      if (formData.contract_date) data.append("contract_date", formData.contract_date);
      if (formData.expiry_date) data.append("expiry_date", formData.expiry_date);
      if (formData.expiry_date_check) data.append("expiry_date_check", formData.expiry_date_check);
      if (formData.auto_renewal !== undefined && formData.auto_renewal !== null && formData.auto_renewal !== "") {
        data.append("auto_renewal", formData.auto_renewal);
      }
      if (formData.followup_enabled !== undefined && formData.followup_enabled !== null && formData.followup_enabled !== "") {
        data.append("followup_enabled", formData.followup_enabled);
      }
      if (formData.reminder_enabled !== undefined && formData.reminder_enabled !== null && formData.reminder_enabled !== "") {
        data.append("reminder_enabled", formData.reminder_enabled);
      }
      if (formData.reminder_days_before) data.append("reminder_days_before", formData.reminder_days_before);
      if (formData.renewal_period_months) data.append("renewal_period_months", formData.renewal_period_months);
      if (formData.business_sector_contract_id) data.append("business_sector_contract_id", formData.business_sector_contract_id);
      if (formData.winch_partner_contract_id) data.append("winch_partner_contract_id", formData.winch_partner_contract_id);
      if (formData.file) {
        data.append("file", formData.file);
      }

      const response = await axios.post(`${BASE_URL}/partner-contracts`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add contract";
      return rejectWithValue(message);
    }
  }
);

// Update contract status
export const updateContractStatus = createAsyncThunk(
  "winch_partner_contracts/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(`${BASE_URL}/partner-contracts/${id}/status`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return { id, status, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update contract status";
      return rejectWithValue(message);
    }
  }
);

const WinchPartnerContractsSlice = createSlice({
  name: "winch_partner_contracts",
  initialState: {
    contracts: [],
    businessSectorContracts: [],
    winchPartnerContracts: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contracts
      .addCase(fetchWinchPartnerContracts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        const raw = action.payload?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        state.contracts = list;
      })
      .addCase(fetchWinchPartnerContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Fetch business sector contracts for dropdown
      .addCase(fetchBusinessSectorContractsForDropdown.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectorContractsForDropdown.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businessSectorContracts = action.payload || [];
      })
      .addCase(fetchBusinessSectorContractsForDropdown.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load business sector contracts";
      })
      // Fetch winch partner contracts for dropdown
      .addCase(fetchWinchPartnerContractsForDropdown.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerContractsForDropdown.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchPartnerContracts = action.payload || [];
      })
      .addCase(fetchWinchPartnerContractsForDropdown.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load winch partner contracts";
      })
      // Add contract
      .addCase(addWinchPartnerContract.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addWinchPartnerContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Contract added successfully";
      })
      .addCase(addWinchPartnerContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add contract";
      })
      // Update contract status
      .addCase(updateContractStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateContractStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.data?.message || "Status updated successfully";
        // Update the contract status in the list
        const contractIndex = state.contracts.findIndex(
          (contract) => contract.id === action.payload.id
        );
        if (contractIndex !== -1) {
          state.contracts[contractIndex].status = action.payload.status;
        }
      })
      .addCase(updateContractStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update contract status";
      });
  },
});

export const { clearState } = WinchPartnerContractsSlice.actions;
export default WinchPartnerContractsSlice.reducer;
